/**
 * Legacy heavy `POLYLINE` entity → `RenderableEntity` adapter (TYPED).
 *
 * # Why this is not `lwpolylineFromEntity`
 *
 * A heavy POLYLINE is a different AutoCAD object, and the difference is
 * visible on screen:
 *
 *   - a **2D polyline** (group 70 without bits 8/16/64) is planar at the
 *     entity's elevation with real per-vertex bulge arcs and per-vertex
 *     widths, so it renders through the SAME shared
 *     `buildLwpolylineSceneGeometry` builder LWPOLYLINE uses — one truth for
 *     the bulge/width scene geometry, no cloned math;
 *   - a **3D polyline** (bit 8) carries a real per-vertex Z and has NO
 *     bulges and NO width at all (groups 42 and 40/41 are 2D-polyline fields —
 *     `AcDb2dVertex` declares `startWidth` / `endWidth`, `AcDb3dPolylineVertex`
 *     declares only `position` / `setPosition` / `vertexType`), so its vertices
 *     are fed through verbatim;
 *   - a **polygon mesh** (bit 16) is an M×N grid of vertices and a
 *     **poly-face mesh** (bit 64) is a vertex pool plus FACE records whose
 *     71/72/73/74 indices reference it. Drawing either as one open
 *     vertex-order polyline would draw a zig-zag that exists nowhere in the
 *     drawing, so each is walked as its real wire structure — mesh rows and
 *     columns for a polygon mesh, per-face edge loops for a poly-face mesh.
 *
 * # ONE topology truth (@codex PR #785 round 3, F1/F2/F3)
 *
 * None of that is decided HERE any more. Which records are visible geometry,
 * in what order, which spans are curved, and where the run lands in WCS all
 * come from `@dxf-ai-editor/cad-core`'s `heavyPolylineTopology.ts` —
 * `heavyPolylineRuns` + `heavyPolylineWcsResolver` — the same answers the
 * picker (`selectLeaf.ts`) and the vector-PDF plot (`heavyPolylineConverter`)
 * read. Three consequences this adapter used to get wrong on its own:
 *
 *   - a **spline-fit** polyline (group 70 bit 4) stores its generated
 *     approximation vertices (VERTEX bit 8) AND its control FRAME (bit 16);
 *     connecting the raw record run drew a phantom segment from the end of
 *     the curve back to the frame, then the frame itself (`abi1-test.dxf`
 *     handle `97F1`: 1 frame record, 121 generated, then 17 more frame);
 *   - a 2D polyline with a non-default group-210 extrusion stores **OCS**
 *     vertices, which this adapter mapped straight into the scene as WCS, so
 *     the canvas landed somewhere the WCS bounds, the viewport-membership
 *     test and the plot did not;
 *   - the per-span bulges must follow the FILTERED record order, not the raw
 *     index.
 *
 * ## A tilted-OCS heavy POLYLINE nested in a BLOCK — CLOSED (@codex round 4)
 *
 * The OCS lift above is applied to the entity this adapter is handed. For a
 * TOP-LEVEL polyline that is the entity itself and the result matches
 * `computeEntityWcsBounds` exactly (pinned by
 * `../__tests__/heavyPolylineOcsLift.property.test.ts` over generated group-210
 * normals). For a BLOCK CHILD the adapter is handed the clone
 * `transformBlockChild.ts` produced, and that clone used to map the child's OCS
 * x/y through the INSERT affine WITHOUT consuming its `extrusion` — so the lift
 * landed AFTER the affine here, while `wcsBoundsBlock.ts` applies it BEFORE
 * (`applyOwnOcs(heavyPolylineLocal(child, true))`). Those two orders differ
 * whenever both the extrusion is non-default AND the INSERT is not the identity,
 * so the child drew where the bounds walk did not.
 *
 * The block arm now consumes the child's OCS itself, through the shape the
 * previous round named: `drawing-model`'s `ocsPlanarTransform.ts`, ezdxf's
 * `OCSTransform` — lift to WCS, apply the affine, then re-express in the
 * TRANSFORMED plane's own OCS (a new group-210 normal). The clone stays a real
 * single-elevation 2D polyline, so the bulges survive; converting it to a 3D
 * polyline instead would have dropped them (group 42 is a 2D-polyline field),
 * which is exactly the fidelity loss CLAUDE.md ПРИОРИТЕТ #1 forbids. Nothing
 * changes HERE — this adapter keeps lifting whatever group 210 it is handed —
 * and the agreement is pinned by
 * `./__tests__/blockChildPolylineOcs.property.test.ts`.
 *
 * # Bulge arcs reach the canvas
 *
 * The bulges travel on `SceneGeometry.polyline.bulges` and `batchedLayer.ts`
 * TESSELLATES them (`../geom/polylineWidthBand.ts::polylineCentrelineSamples`,
 * in the entity's own OCS plane when `extrusion` says so), the same division of labour the
 * `arc` / `circle` / `ellipse` kinds already use: the adapter emits the
 * PARAMETRIC geometry, the batcher turns it into line vertices. Tessellating
 * here instead would destroy the vertex topology every OSNAP handler reads off
 * `geometry.points` (every sample would become an endpoint / midpoint / segment
 * candidate).
 *
 * # Mesh wireframe fidelity
 *
 * AutoCAD draws an unsmoothed polygon mesh as its M×N wireframe and a
 * poly-face mesh as its face edges, honouring the group-70 closed-in-M (bit
 * 1) and closed-in-N (bit 32) wrap flags and a face record's NEGATIVE vertex
 * index (an invisible edge — Autodesk DXF reference, VERTEX group 71). All of
 * that is reproduced by the shared topology.
 *
 * A SMOOTHED polygon mesh (group 75 quadratic / cubic B-spline / Bezier) draws
 * its FITTED SURFACE, and this module used to claim the opposite — that
 * painting the control net "is exactly what AutoCAD shows with SPLFRAME on".
 * It is not: SPLFRAME off is the default, and the surface AutoCAD shows for it
 * is ALREADY IN THE FILE. `PEDIT Smooth` generates the surface vertices, marks
 * them VERTEX group-70 bit 8 and demotes the authored net to bit-16 control
 * points, exactly as a 2D spline-fit polyline does (measured: a 3×4 net
 * smoothed to a cubic surface at 5×6 stores 30 bit-8 records then 12 bit-16
 * records, with `73 = 5  74 = 6  75 = 6`). So there is no evaluator here and
 * no tessellation: `heavyPolylineRuns` now grids the bit-8 records at the
 * group-73/74 densities and excludes the frame — the SAME visible-record
 * filter this adapter's 2D/3D arm already relies on. Nothing here fabricates
 * geometry the file does not contain.
 *
 * A mesh produces several disjoint wire runs, which the `polyline` geometry
 * (ONE connected stroke) cannot express, so it emits the first-class
 * `kind: "mesh-wire"` geometry — see its doc in `../../types.ts` for why the
 * former `block-insert`-plus-satellites shape had to go (it made the mesh's
 * only spatial-index entity look like an INSERT, so object snaps saw a
 * fabricated insertion point at the group-10/20/30 dummy point instead of the
 * real edges).
 */

import {
  hasPolylineWidth,
  heavyPolylineWidths,
  isDefaultExtrusion,
  polylineSubtype,
  type PolylineEntity,
} from "@dxf-ai-editor/drawing-model";
import {
  heavyPolylineRuns,
  heavyPolylineWcsResolver,
} from "@dxf-ai-editor/cad-core";

import { applyOffset } from "../../coordinateOrigin";
import type { RenderableEntity, SceneGeometry, Vec3 } from "../../types";

import {
  boundsFromPoints,
  buildRenderableShell,
  type AdapterContext,
} from "../adapterContext";
import {
  buildLwpolylineSceneGeometry,
  type SceneBounds,
} from "../geom/lwpolylineGeometry";
import { drawnPolylineBandLoops } from "../geom/polylineDrawnBand";
import { thicknessSweepVector } from "../geom/polylineThicknessSweep";
import { polylineCentrelineSamples } from "../geom/polylineWidthBand";

/** Typed adapter for the legacy heavy `POLYLINE` (`polylineEntitySchema`). */
export function polylineFromEntity(
  entity: PolylineEntity,
  ctx: AdapterContext,
): RenderableEntity | null {
  const subtype = polylineSubtype(entity);
  const pointOf = heavyPolylineWcsResolver(entity);
  const runs = heavyPolylineRuns(entity);

  if (subtype === "polygon-mesh" || subtype === "polyface-mesh") {
    const wireRuns: { points: Vec3[]; closed: boolean }[] = [];
    const allPoints: Vec3[] = [];
    for (const run of runs) {
      if (run.indices.length < 2) continue;
      const points = run.indices.map((i) => applyOffset(pointOf(i)));
      allPoints.push(...points);
      wireRuns.push({ points, closed: run.closed });
    }
    if (wireRuns.length === 0) return null;
    return buildRenderableShell(
      entity,
      ctx,
      { kind: "mesh-wire", runs: wireRuns },
      boundsFromPoints(allPoints),
    );
  }

  // A 2D / 3D polyline is ONE run over the visible records (the spline-fit
  // control frame excluded — see the module doc).
  const run = runs[0];
  const indices = run?.indices ?? [];
  const verticesRaw: Vec3[] = indices.map((i) => pointOf(i));

  // A 3D polyline has no bulges (group 42 is a 2D-polyline field), so the
  // shared topology already pins them to 0 for it — this only drops the
  // all-zero list so a plain polyline keeps the exact `{kind, points, closed}`
  // geometry shape it had before arcs existed.
  const bulgesAll = [...(run?.bulges ?? [])];
  const bulges = bulgesAll.some((b) => b !== 0) ? bulgesAll : [];

  // Per-vertex widths (groups 40/41) over the ENTITY-level defaults every
  // vertex without its own 40/41 inherits — `drawing-model`'s
  // `polylineWidth.ts`, the ONE place that rule is written, shared with the
  // LWPOLYLINE adapter and the vector-PDF band. Read through the SAME
  // visible-record `indices` the points are, so a spline-fit polyline's widths
  // stay aligned with its generated run. Emitted only when some segment is
  // actually wide, so a plain polyline keeps the same geometry shape a
  // width-less LWPOLYLINE has.
  //
  // THE SUBTYPE GATE IS INSIDE THE RESOLVER, NOT HERE (@codex PR #785 round 12,
  // F2). Groups 40/41 belong to `AcDb2dPolyline` / `AcDb2dVertex`, so a 3D
  // polyline or a mesh resolves to all-zero pairs and `hasPolylineWidth`
  // collapses them to `[]` — the band builder is never reached and the entity
  // stays its centreline, exactly as `ezdxf`'s `Polyline.has_width`
  // (`if self.is_2d_polyline: … return False`) and its `TraceBuilder`
  // (`raise TypeError("2D POLYLINE required")`) decide. Re-testing the subtype
  // here would be a SECOND copy of that rule; the plot, `cad-core`'s drawn
  // shape and its drawn bounds all read the same resolver and therefore agree
  // by construction.
  const widthsAll = heavyPolylineWidths(entity, indices);
  const widths = hasPolylineWidth(widthsAll) ? widthsAll : [];

  const built = buildLwpolylineSceneGeometry(
    verticesRaw,
    run?.closed ?? false,
    bulges,
    widths,
  );

  // The PLANE the entity's own 2D construction belongs to. `pointOf` has
  // already lifted the vertices OUT of it, and an OCS lift does NOT preserve a
  // transverse (width) direction — for extrusion (0,1,0) the OCS y axis IS
  // world Z, so a band offset in the lifted points' XY draws a flat ribbon
  // where the drawing has one seen edge-on. Declaring the extrusion lets the
  // band be built in the plane and lifted afterwards, which is the order the
  // vector plot already used and the canvas did not (@codex round 5 FINDING B
  // — `../geom/polylinePlane.ts`).
  //
  // Only for the 2D subtype: a 3D polyline / mesh stores REAL WCS vertices and
  // the shared topology reports no OCS for it, so attaching a plane would tilt
  // geometry that was never in one. Only when the extrusion is non-default, so
  // every ordinary polyline keeps the exact geometry shape — and the
  // byte-identical band — it had before.
  //
  // And only when there is something IN-PLANE to build: a WIDTH (the band's
  // transverse offset) or a BULGE (the arc's bow). Round 5 declared the plane
  // for the width alone and left the bulge as a documented residual, which
  // meant a width-LESS bulged run never carried the field at all and
  // `batchedLayer.ts` evaluated its arc on the already-lifted WCS XY: the
  // 5-unit bow of a `(0,0) → (10,0)` bulge-1 semicircle under extrusion
  // `(0,1,0)` was drawn along world Y, where `computeEntityWcsBounds` — which
  // builds the arc in OCS-local coordinates and lifts it — puts it on world Z.
  // The two now answer the same, and `drawing-model` needed no change to make
  // that true; the residual was on this side only.

  // THE GROUP-39 THICKNESS SWEEP (@codex PR #785 round 15, FINDING 3).
  //
  // Group 39 is GEOMETRY: Autodesk's DXF reference defines it as the EXTRUSION
  // of the entity along its extrusion direction, producing a 3D surface, and
  // `cad-core/heavyPolylineDrawnBounds.ts` has indexed exactly that surface
  // since round 9 — the drawn silhouette UNION its copy translated by `t·N̂`.
  // This adapter painted only the base half, so with an OBLIQUE group-210
  // normal the spatial index and the selection box claimed XY extent the canvas
  // never drew in (extrusion (1,1,1), thickness 5, constant width 4 on
  // `(0,0) → (10,0)`: drawn maxX 3.7032479268758554, painted maxX
  // 0.8164965809277261). The index and the canvas must describe ONE shape, and
  // per CLAUDE.md ПРИОРИТЕТ #1 the parity answer is to paint the surface, not
  // to shrink the box.
  //
  // 2D SUBTYPE ONLY. Autodesk's OCS section lists 3DPOLYLINE and MESH among the
  // entities that "do not lie in a particular plane" and states that "of these
  // entities, only lines and points can be extruded", so a 3D polyline and both
  // mesh subtypes have NO thickness surface — fabricating one would draw
  // geometry the file does not contain. (`entityModelBounds` sweeps those
  // subtypes anyway; that is an over-report in the safe direction, pre-existing
  // and untouched here.)
  //
  // The VECTOR is resolved once, through `drawing-model`'s own
  // `withThicknessAlongExtrusion` (`../geom/polylineThicknessSweep.ts` reads it
  // off the image of the origin) — the same call both bounds walks sweep with,
  // so the canvas cannot disagree with them about which way it goes. `null`
  // for every thickness-less polyline, which is almost all of them.
  const sweep =
    subtype === "2d"
      ? thicknessSweepVector(entity.thickness, entity.extrusion)
      : null;

  const tilted =
    subtype === "2d" &&
    !isDefaultExtrusion(entity.extrusion) &&
    (widths.length > 0 || bulges.length > 0);
  if (!tilted || built.geometry.kind !== "polyline") {
    if (sweep === null || built.geometry.kind !== "polyline") {
      return buildRenderableShell(entity, ctx, built.geometry, built.bounds);
    }
    // The sweep is a TRANSLATION, so the swept set's AABB is the union of the
    // base box and that box translated — exact, not padded, because an AABB is
    // translation-covariant and every coordinate of a ruled segment attains its
    // extreme at one of the two ends.
    return buildRenderableShell(
      entity,
      ctx,
      { ...built.geometry, thicknessSweep: sweep },
      sweptSceneBounds(built.bounds, sweep),
    );
  }

  const geometry: SceneGeometry = {
    ...built.geometry,
    extrusion: entity.extrusion,
    ...(sweep === null ? {} : { thicknessSweep: sweep }),
  };

  // The AABB is the box of what is actually DRAWN
  // (`../geom/lwpolylineGeometry.ts`), and in a TILTED plane NEITHER of that
  // shared builder's two expansions describes it. Both are measured in the
  // LIFTED XY: the uniform half-width pad, and `expandBoundsForBulges`'
  // per-span `bulgeSegmentBounds`. Under extrusion (0,1,0) the real bow of a
  // `(0,0) → (10,0)` bulge-1 semicircle is on world Z (the entity occupies no
  // world-Y extent at all), while the flat box claims y ∈ [0, +5] — five units
  // of phantom, and no guarantee of covering the real thing either. (The sign
  // of that phantom flipped with the r17 P1 orientation fix; the point — that
  // it is a phantom at all — did not. Measured: `bulgeSegmentBounds` on the
  // LIFTED chord (0,0) → (−10,0) with bulge +1 returns y ∈ [0, 5].)
  //
  // So the box is taken from the drawn silhouette ITSELF, which is exact
  // rather than padded: the BAND when the run declares width, the tessellated
  // CENTRELINE when it does not — read through the same two shared calls
  // `batchedLayer.ts` makes for this very geometry, so the box cannot describe
  // a curve the canvas is not on. Nothing is under-reported (the silhouette IS
  // every drawn vertex, so a window/crossing at the band edge or the bow keeps
  // a VISIBLE entity), and nothing is over-reported into a plane the entity
  // never enters.
  //
  // WHICH of the two it is, is NOT decided here. `drawnPolylineBandLoops` is
  // the ONE gate `batchedLayer.ts::widePolylineLineVertices` calls for this
  // very geometry, and it answers `null` in BOTH cases the canvas strokes the
  // centreline in: no width declared at all, AND a declared width whose band
  // degenerates to no loops (every station shorter than
  // `../geom/polylineWidthOutline.ts`' `COINCIDENT_EPS`, so
  // `dropDegenerateSegments` leaves nothing to offset). Gating on
  // `widths.length > 0` instead re-derived only the FIRST half of that rule, so
  // a width-declaring run whose band collapsed fell through to `built.bounds` —
  // the box `../geom/lwpolylineGeometry.ts` measured in the LIFTED XY, which
  // its own doc says is "neither the drawn extent nor a guaranteed superset of
  // it" for a tilted entity — while the canvas went on stroking the in-plane
  // centreline. Measured: OCS vertices `(0, 2e-9) → (0, 0)` with bulge 1 under
  // extrusion (-1, 0, 0) draw a 34-sample bow reaching world y = -1e-9 against
  // a bounds box of EXACTLY `{0, 0, 0, 0}` — an under-report, the direction
  // that drops a VISIBLE entity from a prefilter. `cad-core`'s
  // `heavyPolylineDrawnShape.ts` already carried the fall-through ("No live
  // loops — every span degenerate in its own plane. The canvas strokes the
  // centreline in exactly that case"); this was the one consumer that did not.
  const bandLoops = drawnPolylineBandLoops(geometry);
  const drawn: Vec3[][] = bandLoops ?? [
    [
      ...polylineCentrelineSamples(
        geometry.points,
        geometry.closed,
        geometry.bulges,
        entity.extrusion,
      ),
    ],
  ];

  let bounds: SceneBounds | null = null;
  for (const loop of drawn) {
    if (loop.length === 0) continue;
    const box = boundsFromPoints(loop);
    bounds = bounds
      ? {
          minX: Math.min(bounds.minX, box.minX),
          minY: Math.min(bounds.minY, box.minY),
          maxX: Math.max(bounds.maxX, box.maxX),
          maxY: Math.max(bounds.maxY, box.maxY),
        }
      : box;
  }

  // A run that draws NOTHING (every span degenerate in its own plane) keeps the
  // builder's box — a degenerate AABB the spatial index can still hold, exactly
  // as an empty polyline gets one there.
  const drawnBounds = bounds ?? built.bounds;
  return buildRenderableShell(
    entity,
    ctx,
    geometry,
    sweep === null ? drawnBounds : sweptSceneBounds(drawnBounds, sweep),
  );
}

/**
 * The AABB of a box swept along `sweep` — the union of the box with its own
 * translate.
 *
 * EXACT rather than padded: an AABB is translation-covariant, and the swept set
 * is `{p + s·v : p ∈ base, s ∈ [0, 1]}`, whose every coordinate is LINEAR in
 * `s` and therefore attains its extreme at `s = 0` or `s = 1`. The same
 * sentence `cad-core/heavyPolylineDrawnBounds.ts` writes for its own
 * `[p, p + t·N̂]` fold.
 */
function sweptSceneBounds(base: SceneBounds, sweep: Vec3): SceneBounds {
  return {
    minX: Math.min(base.minX, base.minX + sweep.x),
    minY: Math.min(base.minY, base.minY + sweep.y),
    maxX: Math.max(base.maxX, base.maxX + sweep.x),
    maxY: Math.max(base.maxY, base.maxY + sweep.y),
  };
}
